/* InSPiration — product UI runtime: premium variant picker + native quick-view enhancements.
   Loaded only on storefront contexts that expose product selection / quick add. */
(() => {
  const PICKER_SELECTOR = 'variant-picker.ibs-premium-variant-picker';
  const COLOR_SELECTOR = '.ibs-variant-option--color';
  const SIZE_SELECTOR = '.ibs-variant-option--size';
  const guardedPickers = new WeakSet();

  const fold = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const sizeRank = (value) => {
    const raw = String(value ?? '').trim();
    const key = fold(raw).replace(/\s+/g, '');
    const known = new Map([
      ['xxs', 10], ['xxs/xs', 15], ['xs', 20], ['xs/s', 25],
      ['s', 30], ['s/m', 35], ['sm', 35], ['m', 40],
      ['m/l', 45], ['ml', 45], ['l', 50], ['l/xl', 55],
      ['lxl', 55], ['xl', 60], ['xl/xxl', 65], ['xlxxl', 65],
      ['2xl', 70], ['xxl', 70], ['xxl/3xl', 75], ['3xl', 80],
      ['xxxl', 80], ['4xl', 90], ['xxxxl', 90], ['5xl', 100],
      ['tu', 900], ['unique', 900], ['tailleunique', 900], ['sanstaille', 910],
    ]);

    if (known.has(key)) return known.get(key);
    const firstNumber = raw.match(/\d+(?:[.,]\d+)?/);
    if (firstNumber) return 100 + Number(firstNumber[0].replace(',', '.'));
    return 500;
  };

  const getOptionInputValue = (input) => String(input?.value ?? '').trim();

  const sortSizes = (picker) => {
    const fieldset = picker.querySelector(SIZE_SELECTOR);
    if (!(fieldset instanceof HTMLElement)) return;

    /* The visible order is always the shopping order: smallest → largest, left → right. */
    fieldset.style.setProperty('flex-direction', 'row', 'important');
    fieldset.style.setProperty('direction', 'ltr', 'important');

    const labels = Array.from(fieldset.querySelectorAll('label.ibs-size-chip'));
    if (labels.length < 2) return;

    const ordered = labels.slice().sort((a, b) => {
      const inputA = a.querySelector("input[type='radio']");
      const inputB = b.querySelector("input[type='radio']");
      const valueA = inputA instanceof HTMLInputElement ? getOptionInputValue(inputA) : a.textContent;
      const valueB = inputB instanceof HTMLInputElement ? getOptionInputValue(inputB) : b.textContent;
      const rank = sizeRank(valueA) - sizeRank(valueB);
      return rank || String(valueA).localeCompare(String(valueB), 'fr', { numeric: true, sensitivity: 'base' });
    });

    ordered.forEach((label, index) => {
      const input = label.querySelector("input[type='radio']");
      const value = input instanceof HTMLInputElement ? getOptionInputValue(input) : label.textContent;
      label.style.order = String(sizeRank(value));
      label.dataset.ibsSizeOrder = String(index);
    });

    if (ordered.some((label, index) => label !== labels[index])) {
      ordered.forEach((label) => fieldset.append(label));
    }
  };

  /* Keep the old footer sorter disabled: this asset is the single enhancement layer. */
  window.__ibsUxFinalCorrectionsLoaded = true;

  const inputIsUnavailable = (input) =>
    input.dataset.optionAvailable === 'false' || input.getAttribute('aria-disabled') === 'true';

  const syncNativeAvailability = (picker) => {
    const fieldset = picker.querySelector(SIZE_SELECTOR);
    if (!(fieldset instanceof HTMLElement)) return { selectedUnavailable: false };

    const inputs = Array.from(fieldset.querySelectorAll("input[type='radio']"));
    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      /* Shopify's server-rendered availability attributes are authoritative.
         We only mirror them to the native disabled property and the premium label. */
      const unavailable = inputIsUnavailable(input);
      input.disabled = unavailable;
      input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

      const label = input.closest('label.ibs-size-chip');
      if (label instanceof HTMLElement) {
        label.classList.toggle('is-ibs-unavailable', unavailable);
        label.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        label.removeAttribute('data-ibs-selected');
      }
    });

    const checked = fieldset.querySelector("input[type='radio']:checked");
    return {
      selectedUnavailable:
        checked instanceof HTMLInputElement &&
        (checked.disabled || checked.getAttribute('aria-disabled') === 'true' || checked.dataset.optionAvailable === 'false'),
    };
  };

  const normalizeExclusiveSizeState = (picker, preferredInput = null) => {
    const fieldset = picker.querySelector(SIZE_SELECTOR);
    if (!(fieldset instanceof HTMLElement)) return null;

    const radios = Array.from(fieldset.querySelectorAll("input[type='radio']"));
    let selected =
      preferredInput instanceof HTMLInputElement &&
      fieldset.contains(preferredInput) &&
      preferredInput.checked &&
      !preferredInput.disabled
        ? preferredInput
        : radios.find((radio) => radio.checked && !radio.disabled);

    if (!(selected instanceof HTMLInputElement)) selected = null;

    radios.forEach((radio) => {
      const chip = radio.closest('label.ibs-size-chip');
      if (chip instanceof HTMLElement) chip.removeAttribute('data-ibs-selected');
      if (selected && radio !== selected && radio.checked) radio.checked = false;
    });

    const selectedLabel = fieldset.querySelector('.ibs-variant-option__selected');
    if (selectedLabel instanceof HTMLElement && selected) {
      selectedLabel.textContent = selected.value;
    }

    return selected;
  };

  const selectFirstAvailableSize = (picker) => {
    const fieldset = picker.querySelector(SIZE_SELECTOR);
    if (!(fieldset instanceof HTMLElement)) return false;

    const first = Array.from(fieldset.querySelectorAll("input[type='radio']"))
      .filter((input) => input instanceof HTMLInputElement)
      .sort((a, b) => sizeRank(getOptionInputValue(a)) - sizeRank(getOptionInputValue(b)))
      .find((input) =>
        !input.disabled &&
        input.getAttribute('aria-disabled') !== 'true' &&
        input.dataset.optionAvailable !== 'false'
      );

    if (!(first instanceof HTMLInputElement)) return false;
    const current = fieldset.querySelector("input[type='radio']:checked");
    if (current === first) return false;

    first.click();
    return true;
  };

  const normalizeColorStatuses = (picker) => {
    if (picker.querySelector(SIZE_SELECTOR)) return;

    picker.querySelectorAll('.ibs-color-card').forEach((card) => {
      const input = card.querySelector("input[type='radio']");
      const status = card.querySelector('.ibs-color-card__status');
      if (!(input instanceof HTMLInputElement) || !(status instanceof HTMLElement)) return;
      if (input.dataset.optionAvailable === 'false' || input.getAttribute('aria-disabled') === 'true') {
        status.textContent = 'Épuisé';
      }
    });
  };

  const enhancePicker = (picker, { autoFix = false, preferredInput = null } = {}) => {
    if (!(picker instanceof HTMLElement) || !picker.isConnected) return;

    sortSizes(picker);
    normalizeColorStatuses(picker);
    const { selectedUnavailable } = syncNativeAvailability(picker);
    normalizeExclusiveSizeState(picker, preferredInput);

    if (autoFix && selectedUnavailable && !guardedPickers.has(picker)) {
      guardedPickers.add(picker);
      const changed = selectFirstAvailableSize(picker);
      queueMicrotask(() => guardedPickers.delete(picker));
      if (changed) return;
    }
  };

  const enhanceAll = (root = document) => {
    root.querySelectorAll?.(PICKER_SELECTOR).forEach((picker) => enhancePicker(picker));
  };

  const schedulePickerSync = (picker, preferredInput = null, autoFix = false) => {
    [0, 32, 90].forEach((delay) => {
      setTimeout(() => enhancePicker(picker, { preferredInput, autoFix }), delay);
    });
  };

  const scheduleQuickSync = (root = document) => [0, 40, 120, 260, 520].forEach((delay) => {
    setTimeout(() => enhanceAll(root), delay);
  });

  const bindQuickAddDialog = () => {
    const dialog = document.getElementById('quick-add-dialog');
    if (!(dialog instanceof HTMLElement) || dialog.dataset.ibsSizeOrderBound === 'true') return;

    dialog.dataset.ibsSizeOrderBound = 'true';
    dialog.addEventListener('dialog:open', () => {
      /* At dialog:open Shopify has already injected the product HTML. This is the
         deterministic first-open hook that the old click timers were missing. */
      scheduleQuickSync(dialog);
    });
  };

  const init = () => {
    enhanceAll();
    bindQuickAddDialog();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Block an unavailable size before the label can toggle its hidden radio. */
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest(`${SIZE_SELECTOR} .ibs-size-chip`);
    if (!(chip instanceof HTMLElement)) return;

    const input = chip.querySelector("input[type='radio']");
    if (!(input instanceof HTMLInputElement)) return;

    if (input.disabled || inputIsUnavailable(input)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const picker = input.closest(PICKER_SELECTOR);
    if (!(picker instanceof HTMLElement)) return;

    if (input.matches(`${SIZE_SELECTOR} input[type='radio']`)) {
      if (input.disabled || inputIsUnavailable(input)) return;
      normalizeExclusiveSizeState(picker, input);
      schedulePickerSync(picker, input, false);
      return;
    }

    if (input.matches(`${COLOR_SELECTOR} input[type='radio']`)) {
      /* Native Shopify aborts stale variant requests. Wait for its morph/event instead
         of running a parallel product JSON request. */
      schedulePickerSync(picker, null, false);
    }
  }, true);

  document.addEventListener('variant:update', (event) => {
    const target = event.target;
    const picker = target instanceof HTMLElement && target.matches(PICKER_SELECTOR)
      ? target
      : target instanceof Element
        ? target.closest(PICKER_SELECTOR)
        : null;

    if (picker instanceof HTMLElement) {
      requestAnimationFrame(() => enhancePicker(picker, { autoFix: true }));
    } else {
      requestAnimationFrame(() => enhanceAll(document));
    }
  });

  /* Quick-add HTML is asynchronous. Click passes cover warm/cache paths;
     dialog:open above is the authoritative cold-path hook. */
  document.addEventListener('pointerdown', (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.quick-add__button,[data-ibs-card-quick-add]')) {
      scheduleQuickSync(document);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.quick-add__button,[data-ibs-card-quick-add]')) {
      scheduleQuickSync(document);
    }
  }, true);

  document.addEventListener('shopify:section:load', () => {
    bindQuickAddDialog();
    setTimeout(() => enhanceAll(document), 40);
  });
})();

/* InSPiration — quick-view runtime, loaded only where product cards can invoke quick add. */
/* === ibs-quickview-native-final.liquid === */
(function(){
  if(window.__ibsNativeQuickViewFinalLoaded)return;
  window.__ibsNativeQuickViewFinalLoaded=true;
  /* This section is the single owner of the visible quick-view description. */
  window.__ibsQuickDescriptionFallbackLoaded=true;

  var active={description:'',url:''};
  var observer=null;
  var fallbackTimers=[];

  function clean(value){return(value||'').replace(/\s+/g,' ').trim();}

  function capture(button){
    if(!(button instanceof Element))return;
    var component=button.closest('quick-add-component');
    active.description=component?clean(component.dataset.productDescription||''):'';
    var card=button.closest('.product-card');
    var link=card?card.querySelector("a[ref='productTitleLink'],a[ref='productCardLink'],a[href*='/products/']"):null;
    active.url=link instanceof HTMLAnchorElement?link.href:'';
  }

  function modalProductUrl(modal){
    if(active.url)return active.url;
    var link=modal.querySelector("a[href*='/products/']");
    return link instanceof HTMLAnchorElement?link.href:'';
  }

  function modalDescription(modal){
    if(active.description)return active.description;
    var nativeDescription=modal.querySelector('.product-details .text-block.rte,.product-details .rte');
    return nativeDescription?clean(nativeDescription.textContent||''):'';
  }

  function build(modal){
    var description=modalDescription(modal);
    var url=modalProductUrl(modal);
    if(!description&&!url)return null;

    var block=document.createElement('div');
    block.className='quick-add-product-description';
    block.setAttribute('data-ibs-native-description','');

    var eyebrow=document.createElement('p');
    eyebrow.className='quick-add-product-description__eyebrow';
    eyebrow.textContent='Détails';
    block.appendChild(eyebrow);

    if(description){
      var text=document.createElement('p');
      text.className='quick-add-product-description__text';
      text.textContent=description;
      block.appendChild(text);
    }

    if(url){
      var link=document.createElement('a');
      link.className='quick-add-product-description__link';
      link.href=url;
      link.textContent='Voir la fiche complète →';
      block.appendChild(link);
    }
    return block;
  }

  function removeLegacy(modal){
    modal.querySelectorAll('[data-ibs-native-description],.ibs-quick-description,.ibs-master-quick-description,.ibs-qv-luxe-description').forEach(function(node){node.remove();});
  }

  function install(){
    var modal=document.getElementById('quick-add-modal-content');
    if(!(modal instanceof HTMLElement)||!modal.children.length)return false;

    removeLegacy(modal);
    var block=build(modal);
    if(!block)return false;

    if(matchMedia('(max-width:749px)').matches){
      var picker=modal.querySelector(':scope > variant-picker')||modal.querySelector('variant-picker');
      var form=modal.querySelector(':scope > product-form-component')||modal.querySelector('product-form-component');
      if(picker&&picker.parentNode===modal){modal.insertBefore(block,picker);}
      else if(form&&form.parentNode===modal){modal.insertBefore(block,form);}
      else{modal.appendChild(block);}
    }else{
      var details=modal.querySelector('.product-details');
      if(!(details instanceof HTMLElement))return false;
      var desktopPicker=details.querySelector('variant-picker');
      var desktopForm=details.querySelector('product-form-component');
      if(desktopPicker&&desktopPicker.parentNode){desktopPicker.parentNode.insertBefore(block,desktopPicker);}
      else if(desktopForm&&desktopForm.parentNode){desktopForm.parentNode.insertBefore(block,desktopForm);}
      else{details.appendChild(block);}
    }
    return true;
  }

  /* Shopify's product-form variant handler looks up the first product-form in the
     detached response document. On pages where the cart drawer is rendered first,
     that can be the drawer recommendation instead of the quick-view product. Repair
     the detached document before the native bubbling handler reads it. */
  function protectQuickViewVariantButton(event){
    var target=event.target;
    if(!(target instanceof Element)||!target.closest('#quick-add-dialog'))return;
    var html=event.detail&&event.detail.data&&event.detail.data.html;
    if(!(html instanceof Document||html instanceof Element))return;

    var current=document.querySelector('#quick-add-dialog [ref="addToCartButton"]');
    if(!(current instanceof HTMLElement)||!current.id)return;

    var exact=html.querySelector('#'+CSS.escape(current.id)+'[ref="addToCartButton"]');
    var first=html.querySelector('product-form-component [ref="addToCartButton"]');
    if(exact&&first&&exact!==first){
      first.replaceWith(exact.cloneNode(true));
    }
  }

  function finishObserver(){
    if(observer){observer.disconnect();observer=null;}
    fallbackTimers.forEach(clearTimeout);
    fallbackTimers=[];
  }

  function arm(){
    finishObserver();
    var root=document.getElementById('quick-add-dialog');
    if(root){
      observer=new MutationObserver(function(){if(install())finishObserver();});
      observer.observe(root,{childList:true,subtree:true});
    }
    fallbackTimers=[80,220,520,1100,1800].map(function(delay){
      return setTimeout(function(){if(install())finishObserver();},delay);
    });
  }

  function intent(event){
    var target=event.target;
    if(!(target instanceof Element))return;
    var button=target.closest('.quick-add__button,[data-ibs-card-quick-add]');
    if(!button)return;
    capture(button);
    arm();
  }

  document.addEventListener('variant:update',protectQuickViewVariantButton,true);
  document.addEventListener('pointerdown',intent,true);
  document.addEventListener('click',intent,true);
  document.addEventListener('shopify:section:load',function(){setTimeout(install,30);});
})();
